import { Spin, Tabs } from "antd"
import wizardStyles from "./WizardLayout.module.css"
import { CheckCircleFilled } from "@ant-design/icons"
import { useEffect, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import { useSelector } from "react-redux"
import { modelSelector } from "store/selectors"
import { PageName } from "ui/PageName"
import { ProgressBar } from "ui/ProgressBar"
import { PageTitle } from "layoutsV2/pro/ProNewListingLayout/PageTitle"
import { getStepConfig } from "./constants/stepConfig"
import { EXPERIENCE_TYPES } from "constants/experiences"
import { useExperienceWizard } from "./useExperienceWizard"
import { WizardActionButtons } from "./WizardActionButtons"
import { DetailsStep } from "./steps/DetailsStep"
import { TicketTypeStep } from "./steps/TicketTypeStep"
import { SchedulingStep } from "./steps/SchedulingStep"
import { PricingStep } from "./steps/PricingStep"
import { MediaStep } from "./steps/MediaStep"
import { ResourcesStep } from "./steps/ResourcesStep"
import { LocationStep } from "./steps/LocationStep"
import { AdditionalInfoStep } from "./steps/AdditionalInfoStep"
import { PreviewStep } from "./steps/PreviewStep"
import styles from "layoutsV2/pro/ProNewListingLayout/ProNewListingLayout.module.css"

const STEP_COMPONENTS = {
  details: DetailsStep,
  ticket_type: TicketTypeStep,
  scheduling: SchedulingStep,
  pricing: PricingStep,
  media: MediaStep,
  resources: ResourcesStep,
  location: LocationStep,
  additional_info: AdditionalInfoStep,
  preview: PreviewStep
}

export const WizardLayout = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const trip = useSelector(modelSelector("trip"))
  const isFetching = useSelector((state) => state.trips.trip.loading)
  const { currentStep, isSaving, completedSteps, handleStepClick } = useExperienceWizard()

  const routeType = searchParams.get("route")
  const listingType = searchParams.get("type")
  const isEditMode = Boolean(routeType)

  // Once the full trip loads, always sync ?type= from the actual experience_type on the record.
  // This prevents URL manipulation (e.g. manually changing ?type=private to ?type=ticketed)
  // from showing the wrong step set or sending the wrong type to the API on save.
  // For new experiences (no trip yet), the URL param set at creation time is used.
  useEffect(() => {
    if (trip?.id && trip?.experience_type) {
      const derivedType = trip.experience_type === EXPERIENCE_TYPES.ticketed ? EXPERIENCE_TYPES.ticketed : EXPERIENCE_TYPES.private
      if (listingType !== derivedType) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev)
          next.set("type", derivedType)
          return next
        }, { replace: true })
      }
    }
  }, [trip?.id, trip?.experience_type, listingType, setSearchParams])

  const stepConfig = useMemo(() => getStepConfig(listingType), [listingType])
  const currentConfig = stepConfig.find((s) => s.id === currentStep) || stepConfig[0]

  // Progress reflects how many steps are actually completed, not which step is open.
  const completedCount = stepConfig.filter((s) => completedSteps.has(s.id)).length
  const progressPercent = stepConfig.length > 0
    ? Math.round((completedCount / stepConfig.length) * 100)
    : 0

  const tabItems = useMemo(() => stepConfig.map(({ id, label }) => {
    const isCompleted = completedSteps.has(id)
    const tabLabel = isCompleted ? (
      <span className={styles.tabCompleted}>
        <CheckCircleFilled />
        {label}
      </span>
    ) : label

    return { key: id, label: tabLabel }
  }), [stepConfig, completedSteps])

  const contentRef = useRef(null)

  useEffect(() => {
    let el = contentRef.current?.parentElement
    while (el && el !== document.body) {
      const { overflow, overflowY } = window.getComputedStyle(el)
      if (/(auto|scroll)/.test(overflow + overflowY)) {
        el.scrollTo(0, 0)
        break
      }
      el = el.parentElement
    }
  }, [currentStep])

  const StepComponent = STEP_COMPONENTS[currentStep]
  if (!StepComponent) {
    console.warn(`[WizardLayout] No component registered for step "${currentStep}"`)
    return null
  }

  const pageTitle = isEditMode && trip?.title ? (
    <span>
      {t("listings.edit_new")} · <span style={{ color: "var(--color-primary)" }}>{trip.title}</span>
    </span>
  ) : (
    t("listings.create_new")
  )

  const isLoading = isSaving || isFetching

  return (
    <>
      {isLoading && (
        <div className={wizardStyles.loadingOverlay}>
          <Spin size="large" />
        </div>
      )}
      <div className={styles.layout}>
        <div className={styles.header}>
          {!isEditMode && <ProgressBar percent={progressPercent} />}
          <PageName title={pageTitle} />
          <Tabs activeKey={currentStep} items={tabItems} onChange={handleStepClick} />
        </div>
        <div className={styles.body}>
          <PageTitle title={currentConfig.title} subTitle={currentConfig.subTitle} />
          <div className={styles.pageContent}>
            <div ref={contentRef} style={{ paddingBottom: "100px", maxWidth: "1240px" }} className="overflow-auto">
              <StepComponent />
            </div>
          </div>
        </div>
      </div>
      <WizardActionButtons />
    </>
  )
}
